/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Box,
    Button,
    Container,
    Stack,
    Text,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {FormattedMessage, useIntl} from 'react-intl'
// import {useCheckout} from '@salesforce/retail-react-app/app/pages/checkout/util/checkout-context'
import {useCheckout} from '../../checkout/util/checkout-context'

import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../../components/toggle-card'
import Field from '../../../components/field'
import LoginState from './login-state'
import {AuthModal, EMAIL_VIEW, PASSWORD_VIEW, useAuthModal} from '../../../hooks/use-auth-modal'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerBaskets,
    useCustomerId,
    useShopperBasketsMutation,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    API_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    PASSWORDLESS_ERROR_MESSAGES,
    USER_NOT_FOUND_ERROR
} from '@salesforce/retail-react-app/app/constants'
import useCheckoutLoginFields from '../../../components/forms/useCheckoutLoginFields'
import {
    contactInfoFormStyles,
    contactInfoSummaryEmailPhoneWrapper,
    editEmailButton,
    editEmailLabel,
    editEmailMainLabel,
    editEmailWrapper,
    errorText,
    filledButton,
    forgotPasswordButton,
    outlineButton,
    summaryEmailLabel,
    summaryEmailMainLabel
} from './contact-info.style'
import {pxToRem} from '../../../utills/pxToRem'
import useCustomerSearch from '../../../hooks/useCustomerSearch'
import {SIGN_OUT_FROM_CHECKOUT_CONFIRMATION_DIALOG_CONFIG} from '../../cart/partials/cart-secondary-button-group'
import ConfirmationModal from '../../../components/confirmation-modal'
import {parseApiError} from '../../../components/forms/parseApiError/parseApiError'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {pushAnalyticsEvent} from '../../../../app/utills/analytics'

const ContactInfo = ({isSocialEnabled = false, isPasswordlessEnabled = false, idps = []}) => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const appOrigin = useAppOrigin()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const logout = useAuthHelper(AuthHelpers.Logout)
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')
    const modalProps = useDisclosure()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const updateBasket = useShopperBasketsMutation('updateBasket')

    const [emailCustomerSearch, setEmailCustomerSearch] = useState(false)
    const [mobileForGuest, setMobileForGuest] = useState(false)
    const [hasSearchedCustomer, setHasSearchedCustomer] = useState(false)
    const [searchTrigger, setSearchTrigger] = useState(0)
    const prevAuthType = usePrevious(customer?.customerType)

    const {data: searchCustomData, refetch: refetchCustomerSearch} = useCustomerSearch(
        emailCustomerSearch,
        false
    )

    const {step, STEPS, goToStep, goToNextStep, hasOnlyEgiftCards} = useCheckout()

    const form = useForm({
        defaultValues: {
            email: customer?.email || basket?.customerInfo?.email || '',
            password: '',
            mobile:
                basket?.c_subscribePhone ||
                customer?.phoneMobile ||
                (!isServer ? localStorage.getItem('guestMobileNumber') || '' : '')
        }
    })

    const fields = useCheckoutLoginFields({form})
    const emailRef = useRef()
    const mobileRef = useRef()

    const [error, setError] = useState(null)
    const [showPasswordField, setShowPasswordField] = useState(false)
    const [showMobileField, setShowMobileField] = useState(false)
    const [signOutConfirmDialogIsOpen, setSignOutConfirmDialogIsOpen] = useState(false)

    const [authModalView, setAuthModalView] = useState(PASSWORD_VIEW)
    const authModal = useAuthModal(authModalView)
    const customerId = useCustomerId()
    const [isPasswordlessLoginClicked, setIsPasswordlessLoginClicked] = useState(false)
    const passwordlessConfigCallback = getConfig().app.login?.passwordless?.callbackURI
    const callbackURL = isAbsoluteURL(passwordlessConfigCallback)
        ? passwordlessConfigCallback
        : `${appOrigin}${passwordlessConfigCallback}`

    const {data: baskets, isSuccess: isSuccessCustomerBaskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')

    const handlePasswordlessLogin = async (email) => {
        try {
            const redirectPath = window.location.pathname + (window.location.search || '')
            await authorizePasswordlessLogin.mutateAsync({
                userid: email,
                callbackURI: `${callbackURL}?redirectUrl=${redirectPath}`
            })
            setAuthModalView(EMAIL_VIEW)
            authModal.onOpen()
        } catch (error) {
            const message = USER_NOT_FOUND_ERROR.test(error.message)
                ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                : formatMessage(API_ERROR_MESSAGE)
            setError(message)
        }
    }

    const submitForm = async (data) => {
        const valid = await form.trigger('mobile')
        if (valid) {
            setError(null)
            setMobileForGuest(form.getValues('mobile'))
            if (isPasswordlessLoginClicked) {
                handlePasswordlessLogin(data.email)
                setIsPasswordlessLoginClicked(false)
                return
            }
            try {
                if (!data.password) {
                    // update email to cudtomer for basket
                    updateCustomerForBasket.mutate(
                        {
                            parameters: {basketId: basket.basketId},
                            body: {email: data.email}
                        },
                        {
                            onSuccess: async () => {
                                // update basket to update mobile number
                                updateBasket.mutate(
                                    {
                                        parameters: {basketId: basket.basketId},
                                        body: {c_subscribePhone: form.getValues('mobile')}
                                    },
                                    {
                                        onSuccess: async () => {
                                            if (hasOnlyEgiftCards) {
                                                goToStep(STEPS.PAYMENT)
                                            } else {
                                                goToNextStep()
                                            }
                                            setError('')
                                        },
                                        onError: (e) => {
                                            setError('Something went wrong, Try again!')
                                        }
                                    }
                                )
                            },
                            onError: (e) => {
                                setError('Something went wrong, Try again!')
                            }
                        }
                    )
                } else {
                    await login.mutateAsync({username: data.email, password: data.password})
                    if (typeof window !== 'undefined') {
                        localStorage.removeItem('passwordResetEmail')
                    }
                    if (typeof window !== 'undefined' && window.dataLayer) {
                        await pushAnalyticsEvent({
                            eventName: 'loginToAccount',
                            userId: customerId || (customer?.customerId ?? ''),
                            email: data.email,
                            registered: true,
                            phone: customer?.phoneMobile || ''
                        })
                    }

                    const hasBasketItem = basket.productItems?.length > 0
                    const hasGiftcertificate = basket?.giftCertificateItems?.length > 0

                    if (hasBasketItem || hasGiftcertificate) {
                        mergeBasket.mutate({
                            parameters: {
                                createDestinationBasket: true,
                                productItemMergeMode: 'sum_quantities'
                            }
                        })
                    }
                    if (hasOnlyEgiftCards) {
                        goToStep(STEPS.PAYMENT)
                    } else {
                        goToNextStep()
                    }
                }
                // goToNextStep()
            } catch (error) {
                const errorBody = await parseApiError(error, form)
                if (/Unauthorized/i.test(errorBody?.status_code)) {
                    setError(
                        formatMessage({
                            defaultMessage: 'Password incorrect, please try again. ',
                            id: 'contact_info.error.incorrect_password'
                        })
                    )
                } else {
                    setError(errorBody?.message)
                }
            }
        }
    }

    const togglePasswordField = () => {
        if (error) {
            setError(null)
        }
        setShowPasswordField(!showPasswordField)

        refetchCustomerSearch()

        if (emailRef.current) {
            emailRef.current.focus()
        }
    }

    const editEmailForGuest = () => {
        setShowMobileField(false)
    }

    const handleEmailSubmit = async () => {
        setEmailCustomerSearch(form.getValues('email'))
        const valid = await form.trigger('email')
        if (valid) {
            try {
                const {data} = await refetchCustomerSearch() // 👈 Force refetch

                if (!data?.customerExists) {
                    setError('We could not find any account associated with this email.')
                    setShowPasswordField(false)
                } else {
                    setError('')
                    setShowPasswordField(true)
                }
            } catch (err) {
                setError('Something went wrong while checking your email. Please try again.')
            }
        }
    }

    const handleGuestClick = async () => {
        const valid = await form.trigger('email')
        if (valid) {
            setEmailCustomerSearch(form.getValues('email'))
            setShowMobileField(true)
        }
    }

    const onForgotPasswordClick = () => {
        setAuthModalView(PASSWORD_VIEW)
        authModal.onOpen()
    }

    useEffect(() => {
        if (hasSearchedCustomer && !searchCustomData?.customerExists) {
            setError('We could not find any account associated with this email.')
            setShowPasswordField(false)
        }
        if (searchCustomData?.customerExists) {
            setShowPasswordField(!showPasswordField)
            setError('')
        }
    }, [searchCustomData])
    // }, [searchCustomData?.customerExists])

    useEffect(() => {
        if (!showPasswordField) {
            form.unregister('password')
        }
    }, [showPasswordField])

    const onPasswordlessLoginClick = async () => {
        setIsPasswordlessLoginClicked(true)
    }

    const handleMergeBasket = () => {
        const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
        const hasGiftcertificate = baskets?.baskets?.[0]?.giftCertificateItems?.length > 0

        // we only want to merge basket when the user is logged in as a recurring user
        // only recurring users trigger the login mutation, new user triggers register mutation
        // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
        // if you change logic here, also change it in login page
        const shouldMergeBasket = (hasBasketItem || hasGiftcertificate) && prevAuthType === 'guest'
        if (shouldMergeBasket) {
            try {
                mergeBasket.mutate({
                    headers: {
                        // This is not required since the request has no body
                        // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                        'Content-Type': 'application/json'
                    },
                    parameters: {
                        createDestinationBasket: true,
                        productItemMergeMode: 'sum_quantities'
                    }
                })
            } catch (e) {
                setError('Something went wrong, Try again!')
            }
        }
    }

    // If customer is registered merge the basket
    useEffect(() => {
        if (customer.isRegistered) {
            handleMergeBasket()
        }
    }, [customer.isRegistered])

    // Save mobile number to localStorage when it changes
    useEffect(() => {
        const subscription = form.watch((value, {name}) => {
            if (name === 'mobile' && value.mobile && !customer.isRegistered) {
                localStorage.setItem('guestMobileNumber', value.mobile)
            }
        })
        return () => subscription.unsubscribe()
    }, [form.watch, customer.isRegistered])

    // Load mobile number from basket or localStorage when mobile field is shown
    useEffect(() => {
        if (showMobileField && !isServer) {
            const mobileNumber =
                basket?.c_subscribePhone ||
                customer?.phoneMobile ||
                localStorage.getItem('guestMobileNumber')
            if (mobileNumber) {
                form.setValue('mobile', mobileNumber)
            }
        }
    }, [showMobileField, basket?.c_subscribePhone, customer?.phoneMobile])

    return (
        <ToggleCard
            id="step-0"
            step={1}
            title={formatMessage({
                defaultMessage: 'Contact Info',
                id: 'contact_info.title.contact_info'
            })}
            // editing={step && step === STEPS.CONTACT_INFO}
            editing={step === STEPS.CONTACT_INFO}
            isLoading={form.formState.isSubmitting}
            onEdit={() => {
                if (customer.isRegistered) {
                    modalProps.onOpen()
                } else {
                    goToStep(STEPS.CONTACT_INFO)
                }
            }}
            editLabel={
                customer.isRegistered
                    ? formatMessage({
                          defaultMessage: 'Sign Out',
                          id: 'contact_info.action.sign_out'
                      })
                    : formatMessage({
                          defaultMessage: 'Edit',
                          id: 'toggle_card.action.edit_when_guest'
                      })
            }
        >
            <ToggleCardEdit>
                <form
                    onSubmit={form.handleSubmit(submitForm)}
                    noValidate
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            if (showPasswordField) {
                                return
                            } else if (!showPasswordField && !showMobileField) {
                                e.preventDefault()
                                handleEmailSubmit()
                            } else {
                                e.preventDefault()
                            }
                        }
                    }}
                >
                    <Stack spacing={[pxToRem(20), pxToRem(20), pxToRem(25), pxToRem(25)]}>
                        <Stack position="relative" sx={contactInfoFormStyles}>
                            {/* email field */}
                            {!showPasswordField && !showMobileField && (
                                <>
                                    <Field
                                        {...fields.email}
                                        inputRef={emailRef}
                                        isContactInfo={true}
                                    />
                                    {error && <Text sx={errorText}>{error}</Text>}
                                </>
                            )}

                            {/* email and edit email button */}
                            {showPasswordField && !showMobileField && (
                                <Box mb={pxToRem(5)}>
                                    <Text sx={editEmailMainLabel}>
                                        <FormattedMessage
                                            defaultMessage="Email"
                                            id="contact_info.link.edit_email_main_label"
                                        />
                                    </Text>

                                    <Box sx={editEmailWrapper}>
                                        <Text
                                            width={['90%']}
                                            wordBreak={'break-all'}
                                            sx={editEmailLabel}
                                        >
                                            {emailCustomerSearch}
                                        </Text>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={
                                                showMobileField
                                                    ? editEmailForGuest
                                                    : togglePasswordField
                                            }
                                            sx={editEmailButton}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Edit"
                                                id="contact_info.link.edit_email"
                                            />
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {!showPasswordField && showMobileField && (
                                <Box mb={pxToRem(5)}>
                                    <Text sx={editEmailMainLabel}>
                                        <FormattedMessage
                                            defaultMessage="Email"
                                            id="contact_info.link.edit_email_main_label"
                                        />
                                    </Text>

                                    <Box sx={editEmailWrapper}>
                                        <Text
                                            width={['90%']}
                                            wordBreak={'break-all'}
                                            sx={editEmailLabel}
                                        >
                                            {emailCustomerSearch}
                                        </Text>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={
                                                showMobileField
                                                    ? editEmailForGuest
                                                    : togglePasswordField
                                            }
                                            sx={editEmailButton}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Edit"
                                                id="contact_info.link.edit_email"
                                            />
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* mobile field */}
                            {showMobileField && (
                                <>
                                    <Field
                                        {...fields.mobile}
                                        inputRef={mobileRef}
                                        isContactInfo={true}
                                        type="tel"
                                        autoComplete="off"
                                    />
                                    {error && <Text sx={errorText}>{error}</Text>}
                                </>
                            )}

                            {/* password field */}
                            {showPasswordField && !showMobileField && (
                                <Box sx={{marginTop: `0 !important`}}>
                                    <Field
                                        {...fields.password}
                                        isContactInfo={true}
                                        contactInfoError={error}
                                    />
                                    {error && <Text sx={errorText}>{error} </Text>}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={onForgotPasswordClick}
                                        sx={forgotPasswordButton}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Forgot Password?"
                                            id="contact_info.link.forgot_password_capitalize"
                                        />
                                    </Button>
                                </Box>
                            )}
                        </Stack>

                        <Stack spacing={[pxToRem(15), pxToRem(15), pxToRem(14), pxToRem(14)]}>
                            {!showMobileField && (
                                <LoginState
                                    form={form}
                                    // isSocialEnabled={isSocialEnabled}
                                    isSocialEnabled={false}
                                    isPasswordlessEnabled={isPasswordlessEnabled}
                                    idps={idps}
                                    showPasswordField={showPasswordField}
                                    togglePasswordField={togglePasswordField}
                                    handleEmailSubmit={handleEmailSubmit}
                                    handlePasswordlessLoginClick={onPasswordlessLoginClick}
                                />
                            )}

                            {!showPasswordField && !showMobileField && (
                                <Button
                                    onClick={handleGuestClick}
                                    variant={'outline'}
                                    sx={outlineButton}
                                >
                                    <FormattedMessage
                                        defaultMessage="Checkout as Guest"
                                        id="contact_info.button.checkout_as_guest"
                                    />
                                </Button>
                            )}
                            {showMobileField && (
                                <Button type="submit" variant={'filled'} sx={filledButton}>
                                    <FormattedMessage
                                        defaultMessage="Continue to Shipping"
                                        id="contact_info.button.continue_to_shipping"
                                    />
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </form>
                <AuthModal {...authModal} initialEmail={form.getValues().email} />
            </ToggleCardEdit>
            <ToggleCardSummary>
                <Box sx={contactInfoSummaryEmailPhoneWrapper}>
                    {/* summary email */}
                    <Box
                        width={['90%', '90%', '40%', '40%']}
                        wordBreak={'break-all'}
                        mb={[pxToRem(20), pxToRem(20), pxToRem(0), pxToRem(0)]}
                    >
                        <Text sx={summaryEmailMainLabel}>
                            <FormattedMessage
                                defaultMessage="Email"
                                id="contact_info.link.email_main_label_summary"
                            />
                        </Text>
                        <Text sx={summaryEmailLabel}>
                            {basket?.customerInfo?.email || customer?.email}
                        </Text>
                    </Box>

                    {/* summary phone */}
                    <Box>
                        <Text sx={summaryEmailMainLabel}>
                            <FormattedMessage
                                defaultMessage="Phone"
                                id="contact_info.link.phone_main_label_summary"
                            />
                        </Text>
                        <Text sx={summaryEmailLabel}>
                            {(() => {
                                const phone =
                                    basket?.customerInfo?.phoneMobile ||
                                    customer?.phoneMobile ||
                                    basket?.c_subscribePhone

                                return (
                                    phone &&
                                    `(${phone?.slice(0, 3)}) ${phone?.slice(3, 6)} - ${phone?.slice(
                                        6
                                    )}`
                                )
                            })()}
                        </Text>
                    </Box>
                </Box>
            </ToggleCardSummary>

            <ConfirmationModal
                {...SIGN_OUT_FROM_CHECKOUT_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={async () => {
                    await logout.mutateAsync()
                    navigate('/login')
                    setSignOutConfirmDialogIsOpen(false)
                }}
                onAlternateAction={() => {}}
                {...modalProps}
                isFromCheckoutSignOut={true}
            />
        </ToggleCard>
    )
}

ContactInfo.propTypes = {
    isSocialEnabled: PropTypes.bool,
    isPasswordlessEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string)
}

export default ContactInfo
